import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import {
  type NotificationLevel,
} from "@/lib/notification-catalog";
import { type NotificationType } from "@/hooks/use-notifications";

export interface NotificationPreference {
  type: NotificationType;
  level: NotificationLevel;
}

/**
 * Varsler Fase 2: hent den aktive brukerens varsel-preferanser. Manglende
 * rad for en type = type-default (se NOTIFICATION_TYPES / SQL-resolver).
 */
export function useNotificationPreferences() {
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;

  return useQuery({
    queryKey: ["notification-preferences", userId],
    queryFn: async (): Promise<NotificationPreference[]> => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("type, level")
        .eq("user_id", userId);
      if (error) throw error;
      return (data ?? []) as NotificationPreference[];
    },
    enabled: !!userId,
  });
}

/**
 * Sett (upsert) preferanse for én varseltype for aktiv bruker.
 */
export function useSetNotificationPreference() {
  const queryClient = useQueryClient();
  const { activeUser } = useImpersonation();
  const userId = activeUser?.id ?? null;

  return useMutation({
    mutationFn: async (input: { type: NotificationType; level: NotificationLevel }) => {
      if (!userId) throw new Error("Ingen aktiv bruker");
      const { error } = await supabase
        .from("notification_preferences")
        .upsert(
          {
            user_id: userId,
            type: input.type,
            level: input.level,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,type" },
        );
      if (error) throw error;
    },
    onSettled: async () => {
      await invalidateFor(queryClient, "notification_preferences");
    },
  });
}
